import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Checkbox, InputBase, Popover, Typography } from '@mui/material';
import { CheckBox as CheckedIcon, CheckBoxOutlineBlank as UncheckedIcon } from '@mui/icons-material';
import {
  ProductFilterFieldDef,
  ProductFilterOption,
  ProductFilterValue,
} from '../../utils/productBrowserFilterState';
import { BrandMark } from './BrandMark';

export interface FilterValueMenuProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  field: ProductFilterFieldDef;
  options: ProductFilterOption[];
  value: ProductFilterValue;
  onChange: (value: ProductFilterValue) => void;
  /** Overrides the built-in "click name / tick box" hint (e.g. category's brand-narrowed note). */
  hint?: string | null;
  /** Extra control under the options (the Added filter's custom date). */
  footer?: ReactNode;
}

const MENU_WIDTH = 300;

/**
 * Value menu for one filter pill. Single-select applies and closes; multi
 * stays open so several values can be picked in one visit.
 *
 * Brand-style fields (`exclusiveDefault`) split the row: the name selects
 * only that value, the checkbox adds it to a multi-value selection.
 */
export default function FilterValueMenu({
  open,
  anchorEl,
  onClose,
  field,
  options,
  value,
  onChange,
  hint,
  footer,
}: FilterValueMenuProps) {
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLDivElement | null>(null);
  const searchable = !!field.searchable;
  const listId = `product-filter-${field.key}-options`;
  const optionId = (index: number) => `${listId}-option-${index}`;

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const selected = useMemo(
    () => new Set(Array.isArray(value) ? value : value ? [String(value)] : []),
    [value],
  );

  /**
   * The "All X" escape hatch is a real option row carrying a sentinel empty
   * value, not a sibling of the list. Keeping it inside `rows` is what lets
   * arrow keys, Enter, aria-activedescendant and scrollIntoView all share one
   * index: as a sibling it was reachable by mouse only, and it pushed
   * listRef.children one step out of line with activeIndex.
   */
  const showAllRow = !field.multi && !!field.allLabel && !field.locked;
  const rows = useMemo<ProductFilterOption[]>(
    () => (showAllRow ? [{ value: '', label: field.allLabel as string }, ...visible] : visible),
    [showAllRow, field.allLabel, visible],
  );

  const commit = (next: ProductFilterValue, close: boolean) => {
    onChange(next);
    if (close) onClose();
  };

  /** Row click: single picks, brand-style picks only-this, plain multi toggles. */
  const selectOption = (option: ProductFilterOption) => {
    if (!field.multi) {
      commit(option.value, true);
      return;
    }
    const current = Array.isArray(value) ? value : [];
    if (field.exclusiveDefault) {
      const soleSelection = current.length === 1 && current[0] === option.value;
      commit(soleSelection ? [] : [option.value], false);
      return;
    }
    commit(
      current.includes(option.value) ? current.filter((v) => v !== option.value) : [...current, option.value],
      false,
    );
  };

  /** Checkbox: always additive, never replaces the selection. */
  const toggleOption = (option: ProductFilterOption) => {
    const current = Array.isArray(value) ? value : [];
    commit(
      current.includes(option.value) ? current.filter((v) => v !== option.value) : [...current, option.value],
      false,
    );
  };

  const moveActive = (delta: number) => {
    if (rows.length === 0) return;
    setActiveIndex((prev) => {
      const next = (prev + delta + rows.length) % rows.length;
      const row = listRef.current?.children[next] as HTMLElement | undefined;
      row?.scrollIntoView({ block: 'nearest' });
      return next;
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      moveActive(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      moveActive(-1);
    } else if (event.key === 'Enter') {
      const option = rows[activeIndex];
      if (option) {
        event.preventDefault();
        selectOption(option);
      }
    }
  };

  const resolvedHint =
    hint !== undefined
      ? hint
      : field.multi && field.exclusiveDefault
        ? 'Click a name to select it · tick the box to add more'
        : field.hint;

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          onKeyDown: handleKeyDown,
          sx: {
            mt: 0.75,
            width: MENU_WIDTH,
            p: 0.75,
            overflow: 'hidden',
            boxShadow: '0 14px 36px rgba(15, 23, 42, 0.15)',
          },
        },
      }}
    >
      {searchable && (
        <InputBase
          autoFocus
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setActiveIndex(0);
          }}
          placeholder={field.searchPlaceholder || 'Search'}
          inputProps={{
            'aria-label': `Search ${field.label} options`,
            'aria-controls': listId,
            'aria-activedescendant': rows[activeIndex] ? optionId(activeIndex) : undefined,
          }}
          sx={{
            width: '100%',
            mb: 0.5,
            px: 1.125,
            height: 32,
            fontSize: '0.8125rem',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '4px',
            '&.Mui-focused': { borderColor: 'primary.main' },
          }}
        />
      )}

      {resolvedHint && (
        <Typography variant="caption" sx={{ display: 'block', px: 1.125, pb: 0.5, color: 'text.disabled', lineHeight: 1.45 }}>
          {resolvedHint}
        </Typography>
      )}

      <Box
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={field.label}
        aria-multiselectable={field.multi || undefined}
        sx={{ display: 'flex', flexDirection: 'column', gap: '1px', maxHeight: 270, overflowY: 'auto' }}
      >
        {rows.map((option, index) => {
          if (showAllRow && index === 0) {
            return (
              <Box
                key="__all__"
                component="button"
                type="button"
                role="option"
                id={optionId(index)}
                aria-selected={!value}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setActiveIndex(index)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  flex: '0 0 auto',
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  borderRadius: '4px',
                  px: 1.125,
                  py: 0.75,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: '0.8125rem',
                  fontWeight: !value ? 600 : 400,
                  color: !value ? 'primary.dark' : 'text.primary',
                  bgcolor: index === activeIndex ? 'action.hover' : 'transparent',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {option.label}
              </Box>
            );
          }
          const isSelected = selected.has(option.value);
          return (
            <Box
              key={option.value}
              sx={{
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                flex: '0 0 auto',
                bgcolor: index === activeIndex ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              {field.exclusiveDefault && (
                <Checkbox
                  size="small"
                  checked={isSelected}
                  onChange={() => toggleOption(option)}
                  onMouseEnter={() => setActiveIndex(index)}
                  inputProps={{
                    'aria-label': isSelected ? `Remove ${option.label} from selection` : `Add ${option.label} to selection`,
                  }}
                  sx={{ p: 0.5, ml: 0.25 }}
                />
              )}
              <Box
                component="button"
                type="button"
                role="option"
                id={optionId(index)}
                aria-selected={isSelected}
                onClick={() => selectOption(option)}
                onMouseEnter={() => setActiveIndex(index)}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.125,
                  textAlign: 'left',
                  border: 'none',
                  background: 'none',
                  borderRadius: '4px',
                  px: 1.125,
                  py: 0.75,
                  cursor: 'pointer',
                  font: 'inherit',
                  fontSize: '0.8125rem',
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? 'primary.dark' : 'text.primary',
                }}
              >
                {field.multi && !field.exclusiveDefault && (
                  <Box
                    component={isSelected ? CheckedIcon : UncheckedIcon}
                    aria-hidden
                    sx={{ flex: '0 0 auto', fontSize: 18, color: isSelected ? 'primary.main' : 'text.disabled' }}
                  />
                )}
                {option.mark && <BrandMark mark={option.mark} size={22} fontSize={11} />}
                <Box component="span" sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {option.label}
                </Box>
                {option.hint != null && (
                  <Box component="span" sx={{ flex: '0 0 auto', fontSize: '0.75rem', color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
                    {option.hint}
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
        {visible.length === 0 && (
          <Typography variant="body2" sx={{ px: 1.125, py: 0.875, color: 'text.disabled' }}>
            No matches
          </Typography>
        )}
      </Box>
      {footer}
    </Popover>
  );
}
